import React from 'react';
import { Link } from 'react-router-dom';
import ChatBot from '../Components/ChatBot/ChatBot';
import CampaignSection from '../Components/campaign/CampaignSection';
import Footer from '../Components/footer/Footer';
import FloatingTranslator from '../Components/MultiLanguage/FloatingTranslator';
import TravelHimalayaLogo from '../assets/travel-himalayan-logo.png';

// Component for benefit cards that highlight customer value
const BenefitCard = ({ icon, title, description }) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-500">
    <span className="text-4xl mb-4 block text-indigo-600">{icon}</span>
    <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </div>
);

// Component for testimonial cards with a cleaner design
const TestimonialCard = ({ quote, name, title, image }) => (
  <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-indigo-500">
    <div className="flex mb-4">
      <div className="text-indigo-500 text-4xl mr-2">"</div>
    </div>
    <p className="text-gray-700 mb-4 leading-relaxed">{quote}</p>
    <div className="flex items-center">
      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-lg mr-3">
        {image || name.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-gray-800">{name}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </div>
    </div>
  </div>
);

// Component for stats with a more subtle design
const StatItem = ({ value, label }) => (
  <div className="bg-white/10 backdrop-blur-sm p-5 rounded-lg">
    <p className="text-4xl font-bold text-white mb-1">{value}</p>
    <p className="text-sm text-white/80">{label}</p>
  </div>
);

const About = () => {
  // Customer benefits data
  const benefits = [
    {
      icon: '🏔️',
      title: 'Authentic Local Experience',
      description: 'Trek with native guides who share insider knowledge and hidden gems that typical tourists never see.'
    },
    {
      icon: '💰',
      title: 'Better Value, Lower Cost',
      description: 'Save 30-40% compared to international agencies while enjoying higher quality service and accommodations.'
    },
    {
      icon: '🛡️',
      title: 'Safety & Peace of Mind',
      description: 'Our certified guides have 15+ years of experience and complete safety training annually.'
    },
    {
      icon: '❤️',
      title: 'Travel with Purpose',
      description: 'Your journey directly supports disaster relief and community development in remote villages.'
    },
    {
      icon: '🌿',
      title: 'Personalized Attention',
      description: 'Small groups (max 8) ensure your trek is tailored to your pace, interests and comfort level.'
    },
    {
      icon: '🧘',
      title: 'Mindful Adventure',
      description: 'Incorporate optional meditation and yoga practices in breathtaking Himalayan settings.'
    },
  ];

  // Testimonials data
  const testimonials = [
    {
      quote: "I compared several trek operators and saved over $800 by booking with Mantra Mountain. The experience was actually better than what my friends got with expensive Western companies.",
      name: "Sarah Mitchell",
      title: "Annapurna Circuit Trek"
    },
    {
      quote: "The local guides knew exactly when to push me and when to let me rest. I never felt rushed or held back - it was perfectly paced for my abilities.",
      name: "James Richardson",
      title: "Everest Base Camp Trek"
    },
    {
      quote: "As a solo female traveler, safety was my top concern. The team made me feel completely at ease and I had the adventure of a lifetime.",
      name: "Emma Torres",
      title: "Langtang Valley Trek"
    }
  ];

  // Stats data
  const stats = [
    { value: "40%", label: "Average Savings vs Western Agencies" },
    { value: "98%", label: "5-Star Reviews" },
    { value: "15+", label: "Years of Expert Experience" },
    { value: "0", label: "Safety Incidents" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - More focused on customer value */}
      <section 
        className="relative bg-cover bg-center h-[500px]" 
        style={{ backgroundImage: "url('/path-to-himalayan-bg.jpg')" }}
        aria-label="Himalayan mountain landscape"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/50"></div>
        <div className="relative h-full max-w-6xl mx-auto px-4 md:px-6 flex flex-col justify-center">
          <div className="max-w-xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 mt-12 leading-tight">
              Trek Nepal for 40% Less with Local Experts
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-4 leading-relaxed">
              Experience the authentic Himalayas with our Nepalese guides—safer journeys, better value, and life-changing adventures that give back to mountain communities.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/Packages">
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md font-medium text-base shadow-md transition-all duration-300 focus:ring-2 focus:ring-indigo-300 focus:outline-none">
                  View Trek Packages
                </button>
              </Link>
              <Link to="/contact">
                <button className="bg-transparent border border-white text-white hover:bg-white/10 px-5 py-2 rounded-md font-medium text-base transition-all duration-300 focus:ring-2 focus:ring-white/30 focus:outline-none">
                  Ask a Guide
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Why Travelers Choose Us Over Western Agencies</h2>
            <div className="h-1 w-24 bg-indigo-600 mx-auto mb-"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert local guides, unbeatable value, and authentic experiences you can't find anywhere else.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard 
                key={index}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Testimonials Section */}
    {/* Social Proof - Testimonials Section */}
<section className="py-16 bg-gray-50">
  <div className="max-w-6xl mx-auto px-2 md:px-6">
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-gray-900 mb-3">What Our Trekkers Say</h2>
      <div className="h-1 w-24 bg-indigo-600 mx-auto mb-4"></div>
    </div>
    
    <div className="grid md:grid-cols-3 gap-6">
      {testimonials.map((testimonial, index) => (
        <TestimonialCard 
          key={index}
          quote={testimonial.quote}
          name={testimonial.name}
          title={testimonial.title}
          image={testimonial.image}
        />
      ))}
    </div>
    
    <div className="text-center mt-4">
      {/* Change Link to anchor tag with the Google Maps URL */}
      <a href="https://www.google.com/maps/place/Mantra+Mountain+Nepal+Treks+PVT+LTD/@28.397455,84.1301506,746866m/data=!3m1!1e3!4m8!3m7!1s0x2d7c9ad462495687:0xad66f41f291153ac!8m2!3d28.397455!4d84.1301506!9m1!1b1!16s%2Fg%2F11wm_5m4j1?entry=ttu&g_ep=EgoyMDI1MDIxOS4xIKXMDSoJLDEwMjExNDU1SAFQAw%3D%3D" target="_blank" rel="noopener noreferrer">
        <button className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center mx-auto">
          Read more reviews
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </a>
    </div>
  </div>
</section>


      {/* Trust Indicators - Stats Section */}
      <section className="bg-gradient-to-r from-indigo-700 to-purple-700 py-4">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <StatItem key={index} value={stat.value} label={stat.label} />
            ))}
          </div>
        </div>
      </section>

      {/* Our Story - More concise */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Story</h2>
              <div className="h-1 w-24 bg-indigo-600 mb-4"></div>
              <p className="text-gray-600 mb-4">
                Founded by native Nepali trekking guides with over 15 years of experience, Mantra Mountain was born from a simple observation: international travelers were paying too much for treks that often missed the authentic Himalayan experience.
              </p>
              <p className="text-gray-600 mb-4">
                After the devastating 2015 earthquake, we committed to channeling our trekking expertise into rebuilding mountain communities while offering travelers more authentic, affordable adventures with deeper impact.
              </p>
              <p className="text-gray-600">
                Today, we're proud to offer treks that cost 30-40% less than Western agencies while providing superior experiences and directly supporting the communities you'll visit.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What Makes Us Different
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">✓</span> 
                  <span className="text-gray-600">100% local team with intimate knowledge of trails, weather, and culture</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">✓</span> 
                  <span className="text-gray-600">No middlemen or foreign agencies adding unnecessary markup</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">✓</span> 
                  <span className="text-gray-600">Flexible itineraries tailored to your pace and interests</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">✓</span> 
                  <span className="text-gray-600">Direct connections with local families for authentic cultural experiences</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">✓</span> 
                  <span className="text-gray-600">20% of profits fund disaster relief and sustainable development projects</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Guides Section - New section highlighting the team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Your Expert Local Guides</h2>
            <div className="h-1 w-24 bg-indigo-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the experienced Sherpa and Nepali guides who will lead your journey
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {/* Guide Card Examples - You would populate this with your actual guides */}
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto mb-3"></div>
              <h3 className="font-medium text-gray-900">Pasang Sherpa</h3>
              <p className="text-sm text-gray-500">Lead Guide, 20+ summits</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto mb-3"></div>
              <h3 className="font-medium text-gray-900">Mingma Dorje</h3>
              <p className="text-sm text-gray-500">Cultural Expert, 15 yrs exp.</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto mb-3"></div>
              <h3 className="font-medium text-gray-900">Lakpa Tenzing</h3>
              <p className="text-sm text-gray-500">Safety Specialist</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto mb-3"></div>
              <h3 className="font-medium text-gray-900">Dawa Yangzum</h3>
              <p className="text-sm text-gray-500">Wellness & Yoga Guide</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link to="/guides">
              <button className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center mx-auto">
                Meet our full team
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Community Impact - Simplified */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Your Trek Makes a Difference</h2>
              <div className="h-1 w-24 bg-indigo-600 mb-4"></div>
              <p className="text-gray-600 mb-4">
                20% of every trek booking directly supports disaster relief and sustainable development projects in the communities you'll visit.
              </p>
              <p className="text-gray-600 mb-4">
                Since 2015, our trekkers have helped:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">•</span> 
                  <span className="text-gray-600">Rebuild 7 schools and 2 health clinics</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">•</span> 
                  <span className="text-gray-600">Fund emergency relief for 1,200+ families</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">•</span> 
                  <span className="text-gray-600">Train 80+ local entrepreneurs in sustainable tourism</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2 mt-1">•</span> 
                  <span className="text-gray-600">Install clean water systems in 15 remote villages</span>
                </li>
              </ul>
              <Link to="/about">
                <button className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center">
                  Learn more about our impact
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-200 h-40 rounded-lg"></div>
              <div className="bg-gray-200 h-40 rounded-lg"></div>
              <div className="bg-gray-200 h-40 rounded-lg"></div>
              <div className="bg-gray-200 h-40 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section - Simplified */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="md:w-1/3 flex justify-center">
              <img
                src={TravelHimalayaLogo}
                alt="Travel Himalaya Nepal"
                className="w-40 h-40 rounded-full shadow-md object-contain p-4 bg-white border border-gray-200"
              />
            </div>
            
            <div className="md:w-2/3">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Official Partnerships</h2>
              <div className="h-1 w-24 bg-indigo-600 mb-4"></div>
              
              <p className="text-gray-600 mb-3">
                As an officially registered Nepali trekking company with the Nepal Tourism Board, we maintain the highest standards of safety and service.
              </p>
              
              <p className="text-gray-600">
                Our humanitarian efforts are coordinated with local NGOs and community leaders to ensure your contribution creates meaningful, sustainable impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - New for addressing common customer concerns */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
            <div className="h-1 w-24 bg-indigo-600 mx-auto mb-4"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">How can you offer treks for so much less?</h3>
              <p className="text-gray-600">
                As a local Nepali company with zero foreign middlemen, we avoid the high overhead and markup of international agencies. We work directly with local services and maintain leaner operations while prioritizing quality.
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Are your treks safe for solo travelers?</h3>
              <p className="text-gray-600">
                Absolutely! Over 40% of our clients are solo travelers. Our guides pay extra attention to solo trekkers, and we can arrange for you to join small groups. Nepal is also consistently rated one of the safest countries for travelers.
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">What fitness level do I need?</h3>
              <p className="text-gray-600">
                We offer treks for all fitness levels, from easy walks to challenging expeditions. Our most popular treks like Annapurna Base Camp can be completed by anyone with basic fitness who can walk 5-6 hours daily with regular breaks.
              </p>
            </div>
            
            <div className="bg-gray-50 p-5 rounded-lg">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Can I customize my trek?</h3>
              <p className="text-gray-600">
                Yes! Unlike large agencies with fixed itineraries, we can customize any aspect of your trek - from pace and duration to accommodations and side excursions. Just let us know your preferences when booking.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link to="/faq">
              <button className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center mx-auto">
                See all frequently asked questions
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - More direct with better buttons */}
      <section className="bg-gradient-to-r from-indigo-600 to-purple-600 py-12">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Ready to Experience the Real Nepal?
          </h2>
          <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
            Book your trek today and save up to 40% while enjoying an authentic adventure with our expert local guides.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/Packages">
              <button className="bg-white text-indigo-600 hover:bg-gray-100 px-5 py-2 rounded-md font-medium text-base shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/30">
                View All Trek Packages
              </button>
            </Link>
            <Link to="/contact">
              <button className="bg-transparent border border-white text-white hover:bg-white/10 px-5 py-2 rounded-md font-medium text-base transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/30">
                Contact a Guide
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Chatbot, Campaign, and Footer Components */}
      <FloatingTranslator />
      <ChatBot />
      <CampaignSection />
      <Footer />
    </div>
  );
};

export default About;